import { useApolloClient } from "@apollo/client";
import {
  UseDownloadQuotePdfOptions_QuoteFragment,
  useUseDownloadQuotePdfOptionsQuery,
  useUseDownloadQuotePdfOptions_GeneratorTemplatesQuery,
} from "./useDownloadQuotePdfOptions.generated.js";
import { getDataOrNull } from "@msys/common";

export const useDownloadQuotePdfOptions = ({
  doc,
}: {
  doc: UseDownloadQuotePdfOptions_QuoteFragment | undefined;
}) => {
  const client = useApolloClient();
  const query = useUseDownloadQuotePdfOptionsQuery({
    client,
  });
  // Separate operation, so a generator failure — the external service being
  // unreachable, or unparseable assignment settings — costs only the generator
  // options. Combined, its non-null root field nulls the whole response and
  // takes the contracting and Sexy options with it.
  const generatorQuery = useUseDownloadQuotePdfOptions_GeneratorTemplatesQuery({
    client,
  });

  const templateOriginQuoteId = doc?.templateOriginQuoteId;
  const hasSelectedCalculations = Boolean(doc?.agreedCalculation);

  const contractingPdfs =
    getDataOrNull(query.data?.organisationContractingPdfs)?.contractingPdfs ??
    [];
  const sexyQuotePdfTemplates = query.data?.organisationSexyQuotesConfig
    ? (getDataOrNull(query.data?.organisationSexyQuotesConfig)?.pdfTemplates ??
      [])
    : [];
  // Not on error, even though the cache may still hold a response: Apollo keeps
  // serving it rather than clearing it, so a refresh that died arrives as
  // `error` alongside the previous read. Reading it would offer template
  // choices this organisation may no longer have — and the cold cache already
  // drops them, so trusting the warm one made the same outage behave two
  // different ways. `integrationEnabled` goes with them: it is not
  // generator-dependent, but it is only ever selected here beside `templates`,
  // and a generator failure fails the whole operation.
  //
  // Deliberately NOT gated on `loading`, unlike the letterhead count. There an
  // unknown count leaves the destructive action *enabled* and the server
  // decides; here an unknown result *removes* the option, and the control
  // consuming `hasPdfGeneratorIntegration` renders nothing without it. Gating
  // on `loading` would blank a working control on every revalidation of a warm
  // cache — the common case — to pre-empt a staleness of at most one refresh
  // whose worst outcome is a generation that fails loudly and by name.
  const generatorResult = generatorQuery.error
    ? null
    : getDataOrNull(generatorQuery.data?.organisationPdfGeneratorTemplates);
  const pdfGeneratorTemplates = generatorResult?.templates ?? [];
  const sexyQuotePdfTemplatesFiltered = sexyQuotePdfTemplates.filter(
    t =>
      templateOriginQuoteId &&
      (t.templateIds ?? []).includes(templateOriginQuoteId)
  );
  const hasContractingTemplates = contractingPdfs.length > 0;
  const hasPdgGeneratorTemplates = pdfGeneratorTemplates.length > 0;
  const hasSexyQuotePdfTemplates = sexyQuotePdfTemplatesFiltered.length > 0;
  const hasPdfGeneratorIntegration = Boolean(
    generatorResult?.integrationEnabled
  );

  return {
    hasContractingTemplates,
    hasPdgGeneratorTemplates,
    hasSexyQuotePdfTemplates,
    hasPdfGeneratorIntegration,

    hasSelectedCalculations,

    contractingPdfs,
    sexyQuotePdfTemplates: sexyQuotePdfTemplatesFiltered,
    pdfGeneratorTemplates,

    loading: query.loading || generatorQuery.loading,
  };
};
